import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { failedResponses } from '../errors/responses';

const c = initContract();

export const NotificationEventDto = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string()
});
export type NotificationEventDto = z.infer<typeof NotificationEventDto>;

export const NotificationRuleDto = z.object({
  id: z.string().optional(),
  notificationEventId: z.string().min(1, 'Event ID is required'),
  targetType: z.string().min(1, 'Target Type is required'),
  targetId: z.string().optional().nullable(),
  scopeCompanyId: z.string().optional().nullable(),
  scopeMspId: z.string().optional().nullable(),
  isActive: z.boolean().optional()
});
export type NotificationRuleDto = z.infer<typeof NotificationRuleDto>;

const NotificationRulesResponse = z.object({
  items: z.array(NotificationRuleDto)
});

export const NotificationTemplateDto = z.object({
  id: z.string().optional(),
  notificationEventId: z.string().uuid('Valid Notification Event ID is required'),
  scopeCompanyId: z.string().optional().nullable(),
  scopeMspId: z.string().optional().nullable(),
  senderName: z.string().optional().nullable(),
  senderEmail: z.string().email('Valid email format').optional().nullable(),
  subjectLiquid: z.string().min(1, 'Subject is required'),
  bodyLiquid: z.string().min(1, 'Body is required')
});
export type NotificationTemplateDto = z.infer<typeof NotificationTemplateDto>;

const NotificationTemplatesResponse = z.object({
  items: z.array(NotificationTemplateDto)
});

export const SyncNotificationPreferenceDto = z.object({
  eventId: z.string().uuid(),
  isEnabled: z.boolean(),
  targetEmails: z.array(z.string()),
  targetWebhooks: z.array(z.string())
});
export type SyncNotificationPreferenceDto = z.infer<typeof SyncNotificationPreferenceDto>;

export const SyncNotificationPreferencesRequest = z.object({
  scopeCompanyId: z.string().uuid().optional().nullable(),
  scopeMspId: z.string().uuid().optional().nullable(),
  preferences: z.array(SyncNotificationPreferenceDto)
});
export type SyncNotificationPreferencesRequest = z.infer<typeof SyncNotificationPreferencesRequest>;

export default c.router({
  getEvents: {
    method: 'GET',
    path: '/notifications/events',
    responses: {
      200: z.array(NotificationEventDto),
      ...failedResponses
    }
  },
  getRules: {
    method: 'GET',
    path: '/notifications/rules',
    responses: {
      200: z.array(NotificationRuleDto),
      ...failedResponses
    }
  },
  createRule: {
    method: 'POST',
    path: '/notifications/rules',
    responses: {
      200: NotificationRuleDto,
      ...failedResponses
    },
    body: NotificationRuleDto
  },
  getTemplates: {
    method: 'GET',
    path: '/notifications/templates',
    responses: {
      200: z.array(NotificationTemplateDto),
      ...failedResponses
    }
  },
  createTemplate: {
    method: 'POST',
    path: '/notifications/templates',
    responses: {
      200: NotificationTemplateDto,
      ...failedResponses
    },
    body: NotificationTemplateDto
  },
  updateRule: {
    method: 'PUT',
    path: '/notifications/rules/:id',
    pathParams: z.object({
      id: z.string().uuid()
    }),
    responses: {
      200: NotificationRuleDto,
      ...failedResponses
    },
    body: NotificationRuleDto
  },
  deleteRule: {
    method: 'DELETE',
    path: '/notifications/rules/:id',
    pathParams: z.object({
      id: z.string().uuid()
    }),
    responses: {
      204: z.any(),
      ...failedResponses
    },
    body: z.any().optional()
  },
  updateTemplate: {
    method: 'PUT',
    path: '/notifications/templates/:id',
    pathParams: z.object({
      id: z.string().uuid()
    }),
    responses: {
      200: NotificationTemplateDto,
      ...failedResponses
    },
    body: NotificationTemplateDto
  },
  deleteTemplate: {
    method: 'DELETE',
    path: '/notifications/templates/:id',
    pathParams: z.object({
      id: z.string().uuid()
    }),
    responses: {
      204: z.any(),
      ...failedResponses
    },
    body: z.any().optional()
  },
  syncPreferences: {
    method: 'POST',
    path: '/notifications/preferences/sync',
    responses: {
      200: z.any(),
      ...failedResponses
    },
    body: SyncNotificationPreferencesRequest
  }
});
